import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useDisciplines } from "@/hooks/useDisciplines";
import { useTranslation } from "@/hooks/useTranslation";
import { formatLocalISODate, parseLocalISODate } from "@/lib/dateUtils";
import type { Discipline } from "@/types/people";
import { CreateDisciplineDialog } from "./CreateDisciplineDialog";
import { SectionHead } from "./person-form/fields";
import type { DisciplineDraft } from "./person-form/personFormSchema";

interface DisciplineSelectorProps {
  value: DisciplineDraft[];
  onChange: (disciplines: DisciplineDraft[]) => void;
  organizationId?: string;
}

/**
 * The person form's disciplines: pick from the org vocabulary, create a new
 * discipline (`CreateDisciplineDialog`), and edit each assignment's dates,
 * notes and primary flag in a popover. One discipline is primary at most; the
 * first one added is primary. A discipline is assigned at most once.
 *
 * Disciplines written through the person form are global — the form does not
 * offer the old per-project / per-business context (not writable through the
 * `create_person` / `update_person` payload).
 */
export function DisciplineSelector({ value, onChange, organizationId }: DisciplineSelectorProps) {
  const { t } = useTranslation("people");
  const { data: disciplines = [] } = useDisciplines(organizationId);
  const [createOpen, setCreateOpen] = useState(false);

  const assigned = useMemo(() => new Set(value.map((d) => d.discipline_id)), [value]);
  const available = disciplines.filter((d) => !assigned.has(d.id));

  const add = (discipline: Pick<Discipline, "id" | "name">) => {
    if (assigned.has(discipline.id)) return;
    onChange([
      ...value,
      {
        discipline_id: discipline.id,
        name: discipline.name,
        start_date: formatLocalISODate(new Date()),
        end_date: null,
        is_primary: value.length === 0,
        notes: "",
      },
    ]);
  };
  const update = (id: string, patch: Partial<DisciplineDraft>) =>
    onChange(value.map((d) => (d.discipline_id === id ? { ...d, ...patch } : d)));
  const setPrimary = (id: string, primary: boolean) =>
    onChange(value.map((d) => ({ ...d, is_primary: d.discipline_id === id ? primary : primary ? false : d.is_primary })));
  const remove = (id: string) => onChange(value.filter((d) => d.discipline_id !== id));

  const nameOf = (draft: DisciplineDraft) => disciplines.find((d) => d.id === draft.discipline_id)?.name ?? draft.name;

  return (
    <section>
      <SectionHead>{t("form.sections.disciplines")}</SectionHead>
      <div className="flex gap-2">
        <Select
          value=""
          onValueChange={(id) => {
            const discipline = disciplines.find((d) => d.id === id);
            if (discipline) add(discipline);
          }}
        >
          <SelectTrigger className="flex-1" aria-label={t("form.discipline.pick")}>
            <SelectValue placeholder={t("form.discipline.pick")} />
          </SelectTrigger>
          <SelectContent>
            {available.map((discipline) => (
              <SelectItem key={discipline.id} value={discipline.id}>
                {discipline.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" onClick={() => setCreateOpen(true)} disabled={!organizationId}>
          <Plus className="h-4 w-4 mr-2" />
          {t("form.discipline.new")}
        </Button>
      </div>

      {value.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label={t("form.discipline.assigned")}>
          {value.map((draft) => (
            <li key={draft.discipline_id} className="flex items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="rounded-r-none">
                    {nameOf(draft)}
                    {draft.is_primary && (
                      <span className="ml-2 text-muted-foreground">{t("common:people.primary")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start">
                  <div className="space-y-3">
                    <p className="font-medium leading-none">{nameOf(draft)}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        {/* Wrapping label: DatePicker takes no id. */}
                        <Label className="block space-y-1">
                          <span>{t("form.discipline.startDate")}</span>
                          <DatePicker
                            value={parseLocalISODate(draft.start_date)}
                            onChange={(date) => update(draft.discipline_id, { start_date: formatLocalISODate(date) })}
                          />
                        </Label>
                      </div>
                      <div className="space-y-1">
                        <Label className="block space-y-1">
                          <span>{t("form.discipline.endDate")}</span>
                          <DatePicker
                            value={parseLocalISODate(draft.end_date)}
                            onChange={(date) => update(draft.discipline_id, { end_date: formatLocalISODate(date) })}
                          />
                        </Label>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`discipline-primary-${draft.discipline_id}`}
                        checked={draft.is_primary}
                        onCheckedChange={(checked) => setPrimary(draft.discipline_id, checked === true)}
                      />
                      <Label htmlFor={`discipline-primary-${draft.discipline_id}`}>
                        {t("form.discipline.primary")}
                      </Label>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`discipline-notes-${draft.discipline_id}`}>{t("form.discipline.notes")}</Label>
                      <Textarea
                        id={`discipline-notes-${draft.discipline_id}`}
                        rows={2}
                        value={draft.notes}
                        onChange={(e) => update(draft.discipline_id, { notes: e.target.value })}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-l-none border-l-0 px-2"
                aria-label={t("form.discipline.remove", { name: nameOf(draft) })}
                onClick={() => remove(draft.discipline_id)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">{t("form.discipline.empty")}</p>
      )}

      {createOpen && organizationId && (
        <CreateDisciplineDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          organizationId={organizationId}
          disciplines={disciplines}
          onCreated={add}
        />
      )}
    </section>
  );
}
